import React, { useCallback, useEffect, useState } from 'react';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import ordersService, {
  type LegDocumentDetail,
  type LegDocumentAttachment,
} from '../../services/orders.service';

/**
 * View + Reject for a single leg document — the order-editing half of the V1 CMS
 * `legdocumentinfo` modal, launched from the View icon next to a require dropdown in
 * DispatchInfoModal.
 *
 * VIEW: fetches metadata + attachments; the PDF and attachment files stream from Azure
 * blob through the authenticated /orders/leg-documents/{recno}/files/{fileRecno} route
 * (fetched as blobs and opened via object URLs, since <img>/new-tab requests can't carry
 * the bearer token).
 *
 * REJECT (only when `canReject`): reason (from the driverpaperwork_rejectionreason lookup)
 * + comments → persists immediately server-side (flips the leg's require flag back to Y,
 * clears the doc recno, defers pay + emails, reconciles invoice requirements). On success
 * `onRejected()` refreshes the pro so the View icon disappears.
 *
 * Renders above DispatchInfoModal (z-50) and its Clear-Leg drawer (z-[60]).
 */
export interface LegDocumentModalProps {
  /** legdocumentinfo recno to view; null closes the modal. */
  recno: number | null;
  legnum: number | string;
  /** Whether the Reject Paperwork section is available (permission-gated by the caller). */
  canReject: boolean;
  onClose: () => void;
  /** Fired after a successful reject; the parent should refresh the pro (the leg's require
   *  flag/recno changed server-side) and typically close the dispatch modal too. */
  onRejected: () => void;
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) =>
  value ? (
    <div className="flex justify-between gap-4">
      <span className="text-muted whitespace-nowrap">{label}</span>
      <span className="text-heading text-right break-words">{value}</span>
    </div>
  ) : null;

/** Fetches an attachment/PDF blob through the authenticated API and returns an object URL,
 *  revoking the previous one. */
const useFileObjectUrl = (recno: number | null) => {
  const load = useCallback(
    async (fileRecno: number): Promise<string> => {
      if (recno == null) throw new Error('no document');
      const blob = await ordersService.getLegDocumentFileBlob(recno, fileRecno, true);
      return URL.createObjectURL(blob);
    },
    [recno],
  );
  return load;
};

/** Inline thumbnail for an image attachment (lazily fetched blob → object URL). */
const ImageThumb: React.FC<{
  recno: number;
  att: LegDocumentAttachment;
  onOpen: (fileRecno: number) => void;
}> = ({ recno, att, onOpen }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked = false;
    let objUrl: string | null = null;
    ordersService
      .getLegDocumentFileBlob(recno, att.fileRecno, true)
      .then((blob) => {
        if (revoked) return;
        objUrl = URL.createObjectURL(blob);
        setUrl(objUrl);
      })
      .catch(() => {
        /* leave as a fallback link below */
      });
    return () => {
      revoked = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [recno, att.fileRecno]);

  if (!url) {
    return (
      <button
        type="button"
        onClick={() => onOpen(att.fileRecno)}
        className="text-blue-600 dark:text-blue-400 hover:underline text-[0.6875rem]"
      >
        {att.filename}
      </button>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(att.fileRecno)} title={att.filename}>
      <img
        src={url}
        alt={att.filename}
        className="h-24 w-auto rounded border border-gray-300 dark:border-gray-600 object-contain"
      />
    </button>
  );
};

const LegDocumentModal: React.FC<LegDocumentModalProps> = ({
  recno,
  legnum,
  canReject,
  onClose,
  onRejected,
}) => {
  const [doc, setDoc] = useState<LegDocumentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reject form state
  const [reasons, setReasons] = useState<string[]>([]);
  const [reject, setReject] = useState(false);
  const [reason, setReason] = useState('');
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);

  const openFile = useFileObjectUrl(recno);

  useEffect(() => {
    if (recno == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDoc(null);
    setReject(false);
    setReason('');
    setComments('');
    ordersService
      .getLegDocument(recno)
      .then((d) => {
        if (!cancelled) setDoc(d);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load document.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recno]);

  useEffect(() => {
    if (recno == null || !canReject) return;
    let cancelled = false;
    ordersService
      .getPaperworkRejectionReasons()
      .then((r) => {
        if (!cancelled) setReasons(r);
      })
      .catch(() => {
        /* reason list is best-effort; the dropdown just stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, [recno, canReject]);

  const handleOpenFile = useCallback(
    async (fileRecno: number) => {
      try {
        const url = await openFile(fileRecno);
        window.open(url, '_blank', 'noopener');
        // Give the new tab time to load before revoking.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch {
        setError('Failed to open file.');
      }
    },
    [openFile],
  );

  const handleReject = useCallback(async () => {
    if (recno == null) return;
    setSaving(true);
    setError(null);
    try {
      await ordersService.rejectLegDocument(recno, { reason, comments });
      onRejected();
    } catch {
      setError('Failed to reject paperwork.');
      setSaving(false);
    }
  }, [recno, reason, comments, onRejected]);

  // Alt+C closes. Reject/approve are deliberate document actions, so no Alt+S.
  // Claim tracks the same condition as the render below, so the hook stays above it.
  useAltHotkeys({ C: onClose }, undefined, { layer: recno != null });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(recno != null);

  if (recno == null) return null;

  const alreadyRejected = !!doc?.rejectedDatetime;

  return (
    <>
      <div className="fixed inset-0 bg-scrim/40 z-[70]" onClick={onClose} aria-label="Close document" />
      <div className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none">
      <div
        role="dialog"
        aria-label={`Leg document ${doc?.doctypeLabel ?? ''}`}
        ref={panelRef}
        style={panelStyle}
        className="pointer-events-auto w-[460px] max-h-[85vh] overflow-y-auto bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
      >
        <ModalHeader
          title={`${doc?.doctypeLabel || 'Leg Document'} (Leg ${legnum})`}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="p-4 text-xs space-y-4">
          {loading && <div className="text-muted">Loading…</div>}
          {error && <div className="text-red-600 dark:text-red-400">{error}</div>}

          {doc && (
            <>
              <div className="space-y-1">
                <Row label="Created:" value={doc.createdDatetime} />
                <Row label="Created By:" value={doc.createdBy} />
                <Row label="Safe to Move?:" value={doc.safeToMove} />
                <Row label="Equipment #:" value={doc.container} />
                <Row label="Chassis #:" value={doc.chassisId} />
                <Row label="Piece Count:" value={doc.pieceCount} />
                <Row label="Lbs:" value={doc.lbs} />
                <Row label="Data Match?:" value={doc.dataMatch} />
                <Row label="Data Comments:" value={doc.dataComments} />
                <Row label="Seal Number:" value={doc.sealNumber} />
                <Row label="Seal Match?:" value={doc.sealMatch} />
                <Row label="POD Signed By:" value={doc.podSignedBy} />
                <Row label="Email Address:" value={doc.podEmailAddress} />
                <Row label="Missing Signature:" value={doc.missingSignatureReason} />
                <Row label="Comments:" value={doc.comments} />
              </div>

              {doc.hasPdf && (
                <div>
                  <button
                    type="button"
                    onClick={() => handleOpenFile(doc.pdfFileRecno)}
                    className="text-blue-600 dark:text-blue-400 hover:underline text-[0.75rem] font-medium"
                  >
                    View PDF
                  </button>
                </div>
              )}

              {doc.attachments.length > 0 && (
                <div>
                  <div className="bg-slate-600 dark:bg-slate-800 text-white text-xs font-semibold px-2 py-1 rounded mb-1.5">
                    Attachments
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {doc.attachments.map((att) =>
                      att.isImage ? (
                        <ImageThumb key={att.fileRecno} recno={recno} att={att} onOpen={handleOpenFile} />
                      ) : (
                        <button
                          key={att.fileRecno}
                          type="button"
                          onClick={() => handleOpenFile(att.fileRecno)}
                          className="text-blue-600 dark:text-blue-400 hover:underline text-[0.6875rem]"
                        >
                          {att.filename}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Reject Paperwork — order-editing action, permission-gated by canReject. */}
              {canReject && (
                <div>
                  <div className="bg-slate-600 dark:bg-slate-800 text-white text-xs font-semibold px-2 py-1 rounded mb-1.5">
                    Reject Paperwork
                  </div>
                  {alreadyRejected ? (
                    <div className="space-y-1">
                      <Row label="Rejected By:" value={doc.rejectedBy} />
                      <Row label="Date/Time:" value={doc.rejectedDatetime} />
                      <Row label="Reason:" value={doc.rejectedReason} />
                      <Row label="Comments:" value={doc.rejectedComments} />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-label">
                        <input
                          type="checkbox"
                          checked={reject}
                          onChange={(e) => setReject(e.target.checked)}
                        />
                        Reject this paperwork
                      </label>
                      {reject && (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-muted whitespace-nowrap">Reason:</span>
                            <select
                              value={reason}
                              onChange={(e) => setReason(e.target.value)}
                              aria-label="Rejection reason"
                              className="flex-1 h-6 px-1 text-[0.6875rem] rounded border border-slate-300 bg-white dark:bg-gray-700 text-slate-900 dark:text-white"
                            >
                              <option value="" />
                              {reasons.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                          </div>
                          <textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Comments"
                            aria-label="Rejection comments"
                            rows={3}
                            className="w-full px-2 py-1 text-[0.6875rem] rounded border border-slate-300 bg-white dark:bg-gray-700 text-slate-900 dark:text-white"
                          />
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={handleReject}
                              disabled={saving}
                              className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {saving ? 'Rejecting…' : 'Reject'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Read-only reject summary when the viewer can't reject but it's already rejected. */}
              {!canReject && alreadyRejected && (
                <div>
                  <div className="bg-slate-600 dark:bg-slate-800 text-white text-xs font-semibold px-2 py-1 rounded mb-1.5">
                    Rejected
                  </div>
                  <div className="space-y-1">
                    <Row label="Rejected By:" value={doc.rejectedBy} />
                    <Row label="Date/Time:" value={doc.rejectedDatetime} />
                    <Row label="Reason:" value={doc.rejectedReason} />
                    <Row label="Comments:" value={doc.rejectedComments} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 flex justify-center">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default LegDocumentModal;
